import type { ReactNode } from 'react';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { StatusPill } from '@/shared/ui/status-pill';
import { cn } from '@/lib/utils';
import { describeDevice, since } from './device';
import type { TeamSession } from './types';

/** The Profile page's card: the same frame ProfileCard draws. */
export function Panel({
  title,
  description,
  action,
  children,
  className,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section
      className={cn(
        'min-w-0 overflow-hidden rounded-2xl border border-[#E3E7ED] bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)]',
        className,
      )}
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-[#F1F4F8] px-[26px] py-[18px]">
        <div className="min-w-0">
          <h2 className="text-[13.5px] font-semibold">{title}</h2>
          {description && (
            <p className="mt-0.5 text-[11.5px] leading-relaxed text-[#8A94A6]">{description}</p>
          )}
        </div>
        {action}
      </header>
      {children}
    </section>
  );
}

/** One device row: icon, what it is, when it signed in, and an action. */
export function SessionRow({
  session,
  now,
  current,
  action,
}: {
  session: TeamSession;
  now: number;
  current?: boolean;
  action?: ReactNode;
}) {
  const { label, icon: Icon } = describeDevice(session);
  return (
    <li className="flex items-center gap-3 px-[26px] py-3">
      <span
        className="flex size-8 flex-none items-center justify-center rounded-lg bg-muted text-[#6B7688]"
        aria-hidden="true"
      >
        <Icon size={15} strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[12.5px] font-medium">{label}</span>
          {current && <StatusPill status="active">This browser</StatusPill>}
        </div>
        <div className="mt-0.5 text-[11px] text-[#8A94A6]">
          Signed in {since(session.issuedAt, now)}
          {session.ipAddress && (
            <>
              {' · '}
              <span className="font-mono">{session.ipAddress}</span>
            </>
          )}
        </div>
      </div>
      {action}
    </li>
  );
}

/**
 * "Are you sure?" before a sign-out somebody cannot take back from here - the
 * other device has to sign in again with its password.
 */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel,
  pending,
  onConfirm,
  onOpenChange,
}: {
  open: boolean;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  pending: boolean;
  onConfirm: () => void;
  onOpenChange: (open: boolean) => void;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" disabled={pending} onClick={onConfirm}>
            {pending ? 'Signing out…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
